/**
 * A team's throughput at a glance (shipcast-o7j.6): its history day by day,
 * and how often each daily count occurred. Small by design — they sit
 * in the team's row beside Scale and WIP, and show the series the simulation
 * samples, so a skewed range or a clean-up spike is visible before a run.
 *
 * Each chart is one image to assistive technology, named by a sentence that
 * carries what the bars show; recharts' own SVG is presentational inside it.
 */

import { useMemo } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { dailyBars, dayCountBars, zeroDayShare, type DayBar, type DayCountBar } from '@/lib/throughputCharts';

export interface ThroughputChartsProps {
  /** Daily items delivered, oldest first — what the simulation samples. */
  values: readonly number[];
  /** The series' last day (`YYYY-MM-DD`) when known; dates the days. */
  lastDay?: string | null;
}

const BAR_FILL = 'var(--color-action-primary)';
const ZERO_FILL = 'var(--color-text-muted)';

export function ThroughputCharts({ values, lastDay = null }: ThroughputChartsProps) {
  const days = useMemo(() => dailyBars(values, lastDay), [values, lastDay]);
  const dayCounts = useMemo(() => dayCountBars(values), [values]);
  const zeroShare = zeroDayShare(values);

  if (values.length === 0) {
    return <p className="text-xs text-text-muted">No throughput yet.</p>;
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <figure className="min-w-0">
        <figcaption className="text-xs text-text-muted">Daily history</figcaption>
        <div role="img" aria-label={describeDays(days)} className="h-14">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days} barCategoryGap={0} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <XAxis
                dataKey="index"
                tickFormatter={(i: number) => days[i]?.monthLabel ?? ''}
                interval={0}
                tickLine={false}
                tickSize={0}
                axisLine={false}
                tick={{ fontSize: 9, fill: 'var(--color-text-muted)' }}
                height={12}
              />
              <Tooltip cursor={false} content={<DayTooltip />} />
              <Bar dataKey="value" fill={BAR_FILL} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </figure>
      <figure className="min-w-0">
        <figcaption className="text-xs text-text-muted">Items per day</figcaption>
        <div role="img" aria-label={describeDayCounts(dayCounts, values.length)} className="h-14">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dayCounts} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <XAxis
                dataKey="value"
                tickLine={false}
                tickSize={0}
                axisLine={false}
                tick={{ fontSize: 9, fill: 'var(--color-text-muted)' }}
                height={12}
              />
              <Tooltip cursor={false} content={<DayCountTooltip />} />
              <Bar dataKey="days" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                {/* Zero days stay visible but read as "nothing delivered". */}
                {dayCounts.map((bar) => (
                  <Cell key={bar.value} fill={bar.value === 0 ? ZERO_FILL : BAR_FILL} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {zeroShare > 0 && (
          <p className="text-xs text-text-muted">{Math.round(zeroShare * 100)}% of days delivered nothing</p>
        )}
      </figure>
    </div>
  );
}

function describeDays(days: readonly DayBar[]): string {
  const values = days.map((d) => d.value);
  const first = days[0]?.date;
  const last = days[days.length - 1]?.date;
  const span = first && last ? ` from ${first} to ${last}` : '';
  return `${days.length} day${days.length === 1 ? '' : 's'}${span}: ${Math.min(...values)} to ${Math.max(...values)} items a day`;
}

function describeDayCounts(bars: readonly DayCountBar[], days: number): string {
  const busiest = bars.reduce((a, b) => (b.days > a.days ? b : a));
  return `${days} days, 0 to ${bars.length - 1} items a day, most often ${busiest.value}`;
}

function DayTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DayBar }> }) {
  const day = active ? payload?.[0]?.payload : undefined;
  if (!day) return null;
  return (
    <div className="rounded-sm border border-border-default bg-bg-elevated px-2 py-1 text-xs shadow-sm">
      {day.date ?? `Day ${day.index + 1}`}: {day.value} item{day.value === 1 ? '' : 's'}
    </div>
  );
}

function DayCountTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DayCountBar }> }) {
  const bar = active ? payload?.[0]?.payload : undefined;
  if (!bar) return null;
  return (
    <div className="rounded-sm border border-border-default bg-bg-elevated px-2 py-1 text-xs shadow-sm">
      {bar.days} day{bar.days === 1 ? '' : 's'} with {bar.value} item{bar.value === 1 ? '' : 's'}
    </div>
  );
}
